import { useState, useCallback } from 'react';
import HeroHeader from './components/HeroHeader';
import LocationCard from './components/LocationCard';
import ParamsCard from './components/ParamsCard';
import ResultCard from './components/ResultCard';
import CarbonLossCard from './components/CarbonLossCard';
import PFChart from './components/PFChart';
import ChinaMap from './components/ChinaMap';
import BackToTop from './components/BackToTop';

export default function Home() {
  const [fetchedTs, setFetchedTs] = useState<number | null>(null);
  const [result, setResult] = useState<{
    ts: number;
    M: number;
    a: number;
    hc: number;
    pf: number;
    isExtrapolated: boolean;
    note: string;
  } | null>(null);
  const [mapSelection, setMapSelection] = useState<{
    province: string;
    city: string;
    nonce: number;
  } | null>(null);

  const handleTsFetched = useCallback((ts: number) => {
    setFetchedTs(ts);
  }, []);

  const handleMapSelectCity = useCallback((province: string, city: string) => {
    setMapSelection({ province, city, nonce: Date.now() });
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCalculate = useCallback(
    (calcResult: {
      ts: number;
      M: number;
      a: number;
      hc: number;
      pf: number;
      isExtrapolated: boolean;
      note: string;
    }) => {
      setResult(calcResult);
    },
    [],
  );

  return (
    <div className="min-h-screen bg-background-50">
      <HeroHeader />

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20light%20green%20organic%20pattern%20on%20soft%20white%20background%2C%20minimalist%20carbon%20molecular%20structure%20illustration%2C%20subtle%20emerald%20gradient%20particles%2C%20clean%20scientific%20aesthetic%2C%20soft%20natural%20light%2C%20low%20contrast%20elegant%20design%20for%20text%20overlay&width=1600&height=900&seq=hero-biochar-light-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/90 via-background-50/70 to-background-50" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 border border-primary-200 mb-6">
            <i className="ri-leaf-line text-primary-600 text-sm" />
            <span className="text-sm font-medium text-primary-800">
              Puro Earth Biochar Methodology Edition 2025 v.2
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-5">
            生物炭碳持久系数
            <span className="text-primary-600"> PF </span>
            计算器
          </h1>

          <p className="text-base md:text-lg text-foreground-700 max-w-2xl mx-auto leading-relaxed mb-8">
            依据 Puro Earth Biochar Methodology Edition 2025 v.2 — Equation 6.4
            <br />
            <span className="font-mono text-sm text-foreground-600 mt-2 block">
              PF = M − a × H/C_org
            </span>
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-earth-line" />
              NASA POWER 数据源
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-400" />
            <span className="flex items-center gap-1.5">
              <i className="ri-thermometer-line" />
              土壤温度自动获取
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-400" />
            <span className="flex items-center gap-1.5">
              <i className="ri-scales-line" />
              Table 6.1 内插
            </span>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="px-4 md:px-10 pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto space-y-6">
          <LocationCard onTsFetched={handleTsFetched} externalLocation={mapSelection} />
          <ParamsCard initialTs={fetchedTs} onCalculate={handleCalculate} />
          <ResultCard result={result} />
          {result && <CarbonLossCard pf={result.pf} />}
          <PFChart />
        </div>
      </section>

      {/* PF Zones Map */}
      <section className="px-4 md:px-10 pb-16 md:pb-24">
        <div className="max-w-5xl mx-auto">
          <ChinaMap onSelectCity={handleMapSelectCity} />
        </div>
      </section>

      {/* Methodology Info */}
      <section className="px-6 md:px-10 py-12 md:py-16 bg-background-100/70 border-t border-background-200/50 scroll-mt-20" id="methodology">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-accent-500/15 flex items-center justify-center">
              <i className="ri-book-open-line text-accent-600 text-xl" />
            </div>
            <h2 className="text-lg font-semibold text-foreground-950">方法学说明</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 shadow-sm">
              <h3 className="font-medium text-foreground-800 mb-2 flex items-center gap-2">
                <i className="ri-function-line text-primary-600" />
                Equation 6.4
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                持久系数计算公式：
                <span className="font-mono text-foreground-800 ml-1">PF = M − a × H/C_org</span>
                <br />
                其中 M 和 a 为根据土壤温度 Ts 从 Table 6.1 线性插值获得的回归参数。
              </p>
            </div>

            <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 shadow-sm">
              <h3 className="font-medium text-foreground-800 mb-2 flex items-center gap-2">
                <i className="ri-database-2-line text-primary-600" />
                Table 6.1 参数
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                回归参数覆盖 Ts = 7–40°C 范围，逐字取自 Puro Earth Biochar Methodology Edition 2025
                v.2 §6.2.2。超出范围时使用边界值外推。
              </p>
            </div>

            <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 shadow-sm">
              <h3 className="font-medium text-foreground-800 mb-2 flex items-center gap-2">
                <i className="ri-global-line text-primary-600" />
                NASA POWER 数据
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                使用 NASA POWER 项目提供的 20 年气候学数据（2001–2020），获取所选土壤层的年度平均
                TSOIL 值作为 Ts 输入。
              </p>
            </div>

            <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 shadow-sm">
              <h3 className="font-medium text-foreground-800 mb-2 flex items-center gap-2">
                <i className="ri-test-tube-line text-primary-600" />
                H/C_org 实测要求
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                H/C_org 必须为该批次生物炭的实测值（方法学 6.2.2 节要求）。该值反映生物炭的芳香化程度，直接影响持久性评估。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 md:px-10 py-10 bg-background-50 border-t border-background-200/50 scroll-mt-20" id="about">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-foreground-600 leading-relaxed mb-4">
            Table 6.1 回归参数（Ts=7~40°C）内置于本页，逐字取自 Puro Earth Biochar Methodology Edition 2025 v.2,
            §6.2.2
          </p>
          <p className="text-sm text-foreground-600 leading-relaxed mb-4">
            模型背景参考：
            <a
              href="https://github.com/puro-earth/PuroBiocharPersistenceEdition2025"
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors"
            >
              puro-earth/PuroBiocharPersistenceEdition2025
            </a>{' '}
            (Azzi et al. 2024, Geoderma)
          </p>
          <p className="text-xs text-foreground-500 mt-4">
            Ts &lt; 7°C 或 &gt; 40°C 时使用边界值外推，结果仅供参考，请人工复核方法学适用范围
          </p>
        </div>
      </footer>

      <BackToTop />
    </div>
  );
}